/*
 * Seeded integer hashes and smooth value noise for page worlds (dunes, speckle, lumpy rocks). One
 * copy for every page. Shipped worlds are built from these numbers (noise.test.ts pins a few), so
 * never change a constant here; a page that wants other numbers passes another seed.
 */

/** A hash of (x, z, seed) in [0, 1]. Seed 0 is the unseeded hash. Integer inputs. */
export function hash2(x: number, z: number, seed = 0): number {
  let h = Math.imul(x, 374761393) ^ Math.imul(z, 668265263) ^ Math.imul(seed, 2147483647)
  h = Math.imul(h ^ (h >>> 13), 1274126177)
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295
}

/** A hash of (x, y, z, seed) in [0, 1]. Integer inputs. */
export function hash3(x: number, y: number, z: number, seed = 0): number {
  let h = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(z, 1440662683) ^ Math.imul(seed, 2147483647)
  h = Math.imul(h ^ (h >>> 13), 1274126177)
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295
}

/** Smooth 2D value noise in [0, 1] with features about `cell` cells across. */
export function valueNoise(x: number, z: number, cell: number, seed = 0): number {
  const gx = Math.floor(x / cell), gz = Math.floor(z / cell)
  const fx = x / cell - gx, fz = z / cell - gz
  const sx = fx * fx * (3 - 2 * fx), sz = fz * fz * (3 - 2 * fz)
  const a = hash2(gx, gz, seed), b = hash2(gx + 1, gz, seed)
  const c = hash2(gx, gz + 1, seed), d = hash2(gx + 1, gz + 1, seed)
  return (a + (b - a) * sx) * (1 - sz) + (c + (d - c) * sx) * sz
}

/** Smooth 3D value noise in [0, 1] with features about `cell` cells across. */
export function valueNoise3(x: number, y: number, z: number, cell: number, seed = 0): number {
  const gx = Math.floor(x / cell), gy = Math.floor(y / cell), gz = Math.floor(z / cell)
  const fx = x / cell - gx, fy = y / cell - gy, fz = z / cell - gz
  const s = (t: number) => t * t * (3 - 2 * t)
  const sx = s(fx), sy = s(fy), sz = s(fz)
  const l = (a: number, b: number, t: number) => a + (b - a) * t
  const c = (i: number, j: number, k: number) => hash3(gx + i, gy + j, gz + k, seed)
  return l(
    l(l(c(0, 0, 0), c(1, 0, 0), sx), l(c(0, 1, 0), c(1, 1, 0), sx), sy),
    l(l(c(0, 0, 1), c(1, 0, 1), sx), l(c(0, 1, 1), c(1, 1, 1), sx), sy),
    sz,
  )
}
